import { IS_WEB, IS_ANDROID, APP_CHANNEL } from '@/config/channel';
import { Capacitor } from '@capacitor/core';

export type DetectedPlatform = 'android' | 'ios' | 'web';

/**
 * V287: Detect platform using RUNTIME Capacitor check + build-time channel config
 *
 * CRITICAL: Runtime detection takes precedence over build-time config to handle
 * edge cases where app loads remote web (due to server.url misconfiguration).
 *
 * Priority:
 * 1. Runtime Capacitor.isNativePlatform() - ALWAYS correct
 * 2. Build-time VITE_APP_CHANNEL - Correct when app loads local assets
 * 3. Fallback to 'web' - Safe default
 */
export function detectPlatform(): DetectedPlatform {
  // V287: CRITICAL - Runtime detection FIRST to catch platform detection failures
  // If Capacitor says we're native, trust it regardless of build-time config
  if (Capacitor.isNativePlatform()) {
    const runtimePlatform = Capacitor.getPlatform();

    if (runtimePlatform === 'android') {
      // V287: Log if runtime disagrees with build-time config
      if (!IS_ANDROID) {
        console.warn('🚨 V287: Runtime platform (android) disagrees with build config (web)');
        console.warn('🚨 V287: This indicates server.url is set in capacitor.config.ts');
        console.warn('🚨 V287: Using runtime detection to fix billing routing');
      }
      return 'android';
    }

    if (runtimePlatform === 'ios') {
      return 'ios';
    }
  }

  // V287: Build-time detection (works when app loads local assets)
  if (IS_ANDROID) return 'android';
  if (IS_WEB) return 'web';

  // V287: Safe fallback
  return 'web';
}

export function isNativeMobile(): boolean {
  const platform = detectPlatform();
  return platform === 'android' || platform === 'ios';
}

export function requiresGoogleBilling(): boolean {
  return detectPlatform() === 'android';
}

export function getPlatformConfig() {
  const platform = detectPlatform();
  
  return {
    platform,
    isNative: isNativeMobile(),
    requiresGoogleBilling: requiresGoogleBilling(),
    supportedFeatures: {
      inAppPurchases: platform === 'android' || platform === 'ios',
      pushNotifications: isNativeMobile(),
    },
  };
}

